import { supabase } from '../lib/supabase';

export const alertsService = {
  async getAll(options = {}) {
    const {
      page = 1,
      limit = 10,
      filter = 'all',
      archived = false
    } = options;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const from = (page - 1) * limit;
    const to = from + limit - 1;

    let query = supabase
      .from('alerts')
      .select('*', { count: 'exact' })
      .overlaps('user_ids', [user.id]);

    if (archived) {
      query = query.not('archived_at', 'is', null);
    } else {
      query = query.is('archived_at', null);
    }

    if (filter === 'unread') {
      query = query.not('read_by', 'cs', `{${user.id}}`);
    } else if (filter === 'read') {
      query = query.contains('read_by', [user.id]);
    }

    query = query
      .order('created_at', { ascending: false })
      .range(from, to);

    const { data, error, count } = await query;

    if (error) throw error;

    return {
      alerts: data || [],
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };
  },

  async getUnreadCount() {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.warn('Auth error in getUnreadCount:', authError);
        return 0;
      }

      const { count, error } = await supabase
        .from('alerts')
        .select('*', { count: 'exact', head: true })
        .overlaps('user_ids', [user.id])
        .is('archived_at', null)
        .not('read_by', 'cs', `{${user.id}}`);

      if (error) {
        console.warn('Error fetching unread count:', error);
        return 0;
      }
      return count || 0;
    } catch (error) {
      console.warn('Unexpected error in getUnreadCount:', error);
      return 0;
    }
  },

  async markAsRead(alertIds) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const ids = Array.isArray(alertIds) ? alertIds : [alertIds];

    for (const alertId of ids) {
      const { data: alert } = await supabase
        .from('alerts')
        .select('read_by')
        .eq('id', alertId)
        .single();

      const readBy = alert?.read_by || [];
      if (!readBy.includes(user.id)) {
        readBy.push(user.id);

        const { error } = await supabase
          .from('alerts')
          .update({ read_by: readBy })
          .eq('id', alertId);

        if (error) throw error;
      }
    }
  },

  async markAsUnread(alertIds) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const ids = Array.isArray(alertIds) ? alertIds : [alertIds];

    for (const alertId of ids) {
      const { data: alert } = await supabase
        .from('alerts')
        .select('read_by')
        .eq('id', alertId)
        .single();

      const readBy = alert?.read_by || [];
      const updatedReadBy = readBy.filter(id => id !== user.id);

      const { error } = await supabase
        .from('alerts')
        .update({ read_by: updatedReadBy })
        .eq('id', alertId);

      if (error) throw error;
    }
  },

  async markAllByFilter(filter) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase
      .from('alerts')
      .select('id, read_by')
      .overlaps('user_ids', [user.id])
      .is('archived_at', null);

    if (filter === 'unread') {
      query = query.not('read_by', 'cs', `{${user.id}}`);
    } else if (filter === 'read') {
      query = query.contains('read_by', [user.id]);
    }

    const { data: alerts, error } = await query;
    if (error) throw error;

    const toUpdate = (alerts || []).filter(a => !(a.read_by || []).includes(user.id));

    for (const alert of toUpdate) {
      const readBy = [...(alert.read_by || []), user.id];
      await supabase.from('alerts').update({ read_by: readBy }).eq('id', alert.id);
    }

    return toUpdate.length;
  },

  async archiveAlerts(alertIds) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const ids = Array.isArray(alertIds) ? alertIds : [alertIds];

    const { error } = await supabase
      .from('alerts')
      .update({ archived_at: new Date().toISOString() })
      .in('id', ids);

    if (error) throw error;
  },

  async archiveAllByFilter(filter) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase
      .from('alerts')
      .select('id')
      .overlaps('user_ids', [user.id])
      .is('archived_at', null)
      .limit(10000);

    if (filter === 'unread') {
      query = query.not('read_by', 'cs', `{${user.id}}`);
    } else if (filter === 'read') {
      query = query.contains('read_by', [user.id]);
    }

    const { data: alerts, error } = await query;
    if (error) throw error;

    if (!alerts || alerts.length === 0) return 0;

    const ids = alerts.map(a => a.id);

    const batchSize = 500;
    for (let i = 0; i < ids.length; i += batchSize) {
      const batch = ids.slice(i, i + batchSize);
      const { error: updateError } = await supabase
        .from('alerts')
        .update({ archived_at: new Date().toISOString() })
        .in('id', batch);
      if (updateError) throw updateError;
    }

    return ids.length;
  },

  subscribeToAlerts(callback) {
    const subscription = supabase
      .channel('alerts')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'alerts'
        },
        (payload) => {
          callback(payload.new);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }
};
